
import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, RotateCcw } from 'lucide-react';
import { Profile, UserRole } from '../../../types/user';
import { getLeadStats } from '../../../lib/leadsManagement';

interface UserLeadStats {
  total: number;
  neu: number;
  kontaktiert: number;
  gesprochen: number;
  termin_fixiert: number;
  abgeschlossen: number;
  kein_interesse: number;
}

interface UserEditModalProps {
  user: Profile;
  onClose: () => void;
  onSave: (userId: string, updates: Partial<Profile>) => void;
  onDelete?: (userId: string) => void;
  onResetPassword?: (email: string) => void;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ user, onClose, onSave, onDelete, onResetPassword }) => {
  const [formData, setFormData] = useState({
    full_name: user.full_name,
    email: user.email,
    role: user.role,
    is_active: user.is_active,
    calcom_url: user.calcom_url || '',
  });

  const [leadStats, setLeadStats] = useState<UserLeadStats>({
    total: 0,
    neu: 0,
    kontaktiert: 0,
    gesprochen: 0,
    termin_fixiert: 0,
    abgeschlossen: 0,
    kein_interesse: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      setLoadingStats(true);
      const stats = await getLeadStats(user.id);
      setLeadStats(stats);
      setLoadingStats(false);
    };
    loadStats();
  }, [user.id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Cal.com-Link nur für Caller speichern, sonst leeren
    onSave(user.id, {
      ...formData,
      calcom_url: formData.role === 'caller' ? formData.calcom_url.trim() || null : null,
    });
    onClose();
  };

  const handleResetPassword = () => {
    const confirmed = window.confirm(
      `Passwort-Reset Email an ${user.email} senden?\n\nDer Benutzer erhält eine Email mit einem Link zum Zurücksetzen des Passworts.`
    );
    if (confirmed && onResetPassword) {
      onResetPassword(user.email);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Benutzer bearbeiten</h2>
            <p className="text-sm text-gray-500 mt-1">Aktualisieren Sie Benutzerinformationen und Einstellungen.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Basic Info - 2 Columns */}
          <div className="grid grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Vollständiger Name
              </label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Max Mustermann"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                E-Mail-Adresse
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="max@example.com"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Rolle
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="user">Benutzer</option>
                <option value="kunde">Kunde</option>
                <option value="caller">Caller</option>
                <option value="admin">Administrator</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Administratoren haben vollen Zugriff. Kunde und Caller sehen nur
                Dashboard, Anfragen und Pipeline – Caller zusätzlich die Cal.com-Terminbuchung.
              </p>
            </div>

            {/* Cal.com-Link – nur relevant für Caller */}
            {formData.role === 'caller' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Cal.com-Buchungslink
                </label>
                <input
                  type="url"
                  value={formData.calcom_url}
                  onChange={(e) => setFormData({ ...formData, calcom_url: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="https://cal.com/webzen-studio/kurz-besprechung-ph"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Ziel des «Termin»-Buttons in der Anfrage-Detailansicht. Lead-Daten
                  werden automatisch angehängt. Leer lassen = Standard-Link
                  (Webzen Studio, Kurz-Besprechung).
                </p>
              </div>
            )}

            {/* Active Status */}
            <div className="flex items-start gap-3 pt-7">
              <div className="flex h-5 items-center">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="is_active" className="block text-sm font-bold text-gray-700 cursor-pointer">
                  Konto ist aktiv
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Deaktivierte Benutzer können sich nicht anmelden.
                </p>
              </div>
            </div>
          </div>

          {/* Reset Password Button */}
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-900 mb-1">Passwort zurücksetzen</p>
                <p className="text-xs text-gray-600">
                  Generiert ein temporäres Passwort. Der Benutzer muss es beim nächsten Login ändern.
                </p>
              </div>
              <button
                type="button"
                onClick={handleResetPassword}
                className="flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 transition-colors whitespace-nowrap"
              >
                <RotateCcw className="h-4 w-4" />
                Zurücksetzen
              </button>
            </div>
          </div>

          {/* User Stats (Read-only) */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Lead-Statistiken</p>
              <p className="text-xs text-gray-500">
                <span className="font-bold">Letzter Login:</span>{' '}
                {user.last_login
                  ? new Date(user.last_login).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Nie'}
              </p>
            </div>
            {loadingStats ? (
              <div className="text-sm text-gray-500">Lade Statistiken...</div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                <div className="bg-white rounded-lg p-2 border border-gray-200">
                  <p className="text-xl font-bold text-gray-900">{leadStats.total}</p>
                  <p className="text-xs text-gray-600 font-medium">Gesamt</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-blue-200">
                  <p className="text-xl font-bold text-blue-600">{leadStats.neu}</p>
                  <p className="text-xs text-blue-700 font-medium">Neu</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-orange-200">
                  <p className="text-xl font-bold text-orange-600">{leadStats.kontaktiert}</p>
                  <p className="text-xs text-orange-700 font-medium">Kont.</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-yellow-200">
                  <p className="text-xl font-bold text-yellow-600">{leadStats.gesprochen}</p>
                  <p className="text-xs text-yellow-700 font-medium">Gespr.</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-purple-200">
                  <p className="text-xl font-bold text-purple-600">{leadStats.termin_fixiert}</p>
                  <p className="text-xs text-purple-700 font-medium">Termin</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-green-200">
                  <p className="text-xl font-bold text-green-600">{leadStats.abgeschlossen}</p>
                  <p className="text-xs text-green-700 font-medium">Abg.</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-red-200">
                  <p className="text-xl font-bold text-red-600">{leadStats.kein_interesse}</p>
                  <p className="text-xs text-red-700 font-medium">K. Int.</p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-3 border-t border-gray-200">
            {onDelete && user.role !== 'admin' && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Möchten Sie ${user.full_name} wirklich löschen?`)) {
                    onDelete(user.id);
                    onClose();
                  }
                }}
                className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-100 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                Speichern
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
